import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import FileUpload from './FileUpload';
import MapboxVisualization from './MapboxVisualization';
import LayerControls from './LayerControls';
import MarketViews, { getMarketPreset, detectActiveMarket } from './MarketViews';
import LayerStats from './LayerStats';
import StateFilter from './StateFilter';
import WinZoneCards from './WinZoneCards';
import { toast } from 'sonner';
import { getLayerConfig } from '../config/layerConfig';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const MapDashboard = ({ apiUrl }) => {
  const [pointData, setPointData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [densityData, setDensityData] = useState([]);
  const [allLayers, setAllLayers] = useState([]);
  const [activeLayers, setActiveLayers] = useState({});
  const [radiusSettings, setRadiusSettings] = useState({});
  const [layerColors, setLayerColors] = useState({});
  const [winZonesMode, setWinZonesMode] = useState(null);
  const [winZoneRankings, setWinZoneRankings] = useState([]);
  const [enrichedFeatures, setEnrichedFeatures] = useState([]);
  const [winZones, setWinZones] = useState([]);
  const [selectedStates, setSelectedStates] = useState(null); // string[] | null
  const [zoneFocus, setZoneFocus] = useState('regional'); // 'local' | 'regional' | 'territory'
  const [topZones, setTopZones] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [winClusters, setWinClusters] = useState([]);
  const [winZoneSettings, setWinZoneSettings] = useState({
    densityFloor: 0.6,
    coverageRadiusMi: 30,
    minClusterSize: 5,
    targetZones: 19,
  });
  const [showWinZoneSettings, setShowWinZoneSettings] = useState(false);
  const [showZoneFocus, setShowZoneFocus] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mapZoomRef = useRef(null);

  const initLayerSettings = (layers, prevActive, prevRadius) => {
    const newActive = { ...prevActive };
    const newRadius = { ...prevRadius };
    layers.forEach(layer => {
      if (!(layer in newActive)) newActive[layer] = false;
      const config = getLayerConfig(layer);
      if (config.radius?.enabled && !newRadius[layer]) {
        newRadius[layer] = { visible: false, miles: config.radius.default };
      }
    });
    return { newActive, newRadius };
  };

  const handleMarketSelect = (marketKey) => {
    const newActive = {};
    allLayers.forEach(l => { newActive[l] = false; });
    if (marketKey && marketKey !== 'custom') {
      const preset = getMarketPreset(marketKey);
      if (preset) {
        preset.layers.forEach(l => { newActive[l] = true; });
        setSelectedStates(preset.states && preset.states.length > 0 ? preset.states : null);
      }
    } else {
      setSelectedStates(null);
    }
    setActiveLayers(newActive);
  };

  const activeMarket = detectActiveMarket(activeLayers, selectedStates);

  const handlePointUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      setLoading(true);
      const response = await axios.post(`${apiUrl}/upload/point`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const layerAdded = response.data.layer_added;
      toast.success(`Uploaded: ${response.data.processed} ${layerAdded || 'points'}`);
      const layers = response.data.layers || [];
      setAllLayers(prev => [...new Set([...prev, ...layers])]);
      const { newActive, newRadius } = initLayerSettings(layers, activeLayers, radiusSettings);
      if (layerAdded) newActive[layerAdded] = true;
      setActiveLayers(newActive);
      setRadiusSettings(newRadius);
      await Promise.all([fetchPointData(), fetchLocationData()]);
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const handleDensityUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      setLoading(true);
      const response = await axios.post(`${apiUrl}/upload/density`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Density data: ${response.data.processed} records merged`);
      const layers = response.data.layers || [];
      setAllLayers(prev => [...new Set([...prev, ...layers])]);
      const { newActive, newRadius } = initLayerSettings(layers, activeLayers, radiusSettings);
      setActiveLayers(newActive);
      setRadiusSettings(newRadius);
      await fetchDensityData();
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const fetchPointData = async () => { try { setPointData((await axios.get(`${apiUrl}/data/point`)).data.data || []); } catch (e) { console.error(e); } };
  const fetchLocationData = async () => { try { setLocationData((await axios.get(`${apiUrl}/data/locations`)).data.data || []); } catch (e) { console.error(e); } };
  const fetchDensityData = async () => { try { setDensityData((await axios.get(`${apiUrl}/data/density`)).data.data || []); } catch (e) { console.error(e); } };

  const fetchTopZones = useCallback(async () => {
    try {
      const activeLayerNames = Object.keys(activeLayers).filter(key => activeLayers[key]);
      const response = await axios.get(`${apiUrl}/analytics/top-zones`, { params: { layers: activeLayerNames.join(',') } });
      setTopZones(response.data.top_zones || []);
      setTotalCount(response.data.total_count || 0);
    } catch (e) { console.error(e); }
  }, [activeLayers, apiUrl]);

  const toggleLayer = (layerKey) => { setActiveLayers(prev => ({ ...prev, [layerKey]: !prev[layerKey] })); };
  const handleRadiusChange = (layer, settings) => { setRadiusSettings(prev => ({ ...prev, [layer]: settings })); };
  const handleColorChange = (layer, color) => { setLayerColors(prev => ({ ...prev, [layer]: color })); };

  const handleZoomToZone = useCallback((zone) => {
    if (mapZoomRef.current && zone.bbox) {
      mapZoomRef.current(zone.bbox);
    }
    setMobileOpen(false);
  }, []);

  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const [pointResp, locResp, densityResp] = await Promise.all([
          axios.get(`${apiUrl}/data/point`), axios.get(`${apiUrl}/data/locations`), axios.get(`${apiUrl}/data/density`)
        ]);
        const pLoaded = pointResp.data.data || [];
        const lLoaded = locResp.data.data || [];
        const dLoaded = densityResp.data.data || [];
        let combined = [];
        if (pLoaded.length > 0) { setPointData(pLoaded); const s = new Set(); pLoaded.forEach(d => Object.keys(d.layers).forEach(l => s.add(l))); combined = [...combined, ...s]; }
        if (lLoaded.length > 0) { setLocationData(lLoaded); const s = new Set(); lLoaded.forEach(d => s.add(d.layer)); combined = [...combined, ...s]; }
        if (dLoaded.length > 0) { setDensityData(dLoaded); const s = new Set(); dLoaded.forEach(d => Object.keys(d.layers).forEach(l => s.add(l))); combined = [...combined, ...s]; }
        const unique = [...new Set(combined)];
        setAllLayers(unique);
        // Default to Dairy market view on initial load
        const defaultPreset = getMarketPreset('dairy');
        const newActive = {};
        unique.forEach(l => { newActive[l] = false; });
        if (defaultPreset) defaultPreset.layers.forEach(l => { if (unique.includes(l)) newActive[l] = true; });
        setActiveLayers(newActive);
        const { newRadius } = initLayerSettings(unique, newActive, {});
        setRadiusSettings(newRadius);
      } catch (e) { console.error(e); }
    };
    loadExistingData();
  }, [apiUrl]);

  useEffect(() => {
    if (pointData.length > 0 || locationData.length > 0 || densityData.length > 0) fetchTopZones();
  }, [fetchTopZones, pointData, locationData, densityData]);

  const hasData = pointData.length > 0 || locationData.length > 0 || densityData.length > 0;

  // Filter data by selected state for stats
  const matchesStateFilter = (stateName) => {
    if (!selectedStates || selectedStates.length === 0) return true;
    const normalized = stateName.trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    return selectedStates.includes(normalized);
  };

  const filteredPointData = useMemo(
    () => (selectedStates ? pointData.filter(d => matchesStateFilter(d.state)) : pointData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStates, pointData]
  );
  const filteredLocationData = useMemo(
    () => (selectedStates ? locationData.filter(d => matchesStateFilter(d.state)) : locationData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStates, locationData]
  );
  const filteredDensityData = useMemo(
    () => (selectedStates ? densityData.filter(d => matchesStateFilter(d.state)) : densityData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStates, densityData]
  );

  const sidebarContent = (
    <>
      {/* Market Views */}
      {hasData && (
        <div className="px-4 py-3 border-b border-stone-100">
          <label className="text-[10px] tracking-[0.08em] uppercase font-semibold text-stone-400 block mb-2">Market Views</label>
          <MarketViews
            activeLayers={activeLayers} allLayers={allLayers}
            onMarketSelect={(key) => { handleMarketSelect(key); setMobileOpen(false); }}
            activeMarket={activeMarket}
          />
        </div>
      )}

      {/* State Filter */}
      {hasData && (
        <div className="px-4 py-2 border-b border-stone-100">
          <StateFilter
            selectedStates={selectedStates}
            onStatesChange={setSelectedStates}
            densityData={densityData}
            locationData={locationData}
          />
        </div>
      )}

      {/* Layer Stats */}
      {hasData && (
        <div className="px-4 py-3 border-b border-stone-100">
          <LayerStats
            activeLayers={activeLayers}
            allLayers={allLayers}
            pointData={filteredPointData}
            locationData={filteredLocationData}
            densityData={filteredDensityData}
            onToggle={toggleLayer}
            activeMarket={activeMarket}
          />
        </div>
      )}

      {/* Win Zones */}
      {hasData && (pointData.length > 0 || locationData.length > 0) && densityData.length > 0 && (
        <div className="px-4 py-3 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-3.5 h-3.5 rounded-full flex-shrink-0 bg-gradient-to-r from-orange-500 to-red-600" />
            <span className={`text-sm flex-1 font-medium ${winZonesMode ? 'text-red-700' : 'text-stone-400'}`}>Win Zones</span>
            <div onClick={(e) => e.stopPropagation()}>
              <Switch checked={!!winZonesMode} onCheckedChange={(checked) => setWinZonesMode(checked ? 'market' : null)} className="scale-75" data-testid="win-zones-toggle" />
            </div>
          </div>
          {winZonesMode && (
            <div className="ml-5 mt-1.5 flex items-center gap-1">
              <button onClick={() => setWinZonesMode('market')} className={`text-[10px] px-2 py-1 rounded transition-colors ${winZonesMode === 'market' ? 'bg-green-700 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`} data-testid="win-mode-market">Market</button>
              <button onClick={() => setWinZonesMode('coverage')} className={`text-[10px] px-2 py-1 rounded transition-colors ${winZonesMode === 'coverage' ? 'bg-purple-700 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`} data-testid="win-mode-coverage">Coverage</button>
              <button onClick={() => setWinZonesMode('opportunity')} className={`text-[10px] px-2 py-1 rounded transition-colors ${winZonesMode === 'opportunity' ? 'bg-orange-600 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`} data-testid="win-mode-opportunity">Opportunity</button>
              <button
                onClick={() => setShowZoneFocus(v => !v)}
                className="text-[10px] text-stone-400 hover:text-stone-600 ml-auto"
              >
                {showZoneFocus ? 'Hide' : 'Adjust'}
              </button>
            </div>
          )}
          {winZonesMode && showZoneFocus && (
            <div className="ml-5 mt-1.5 flex items-center gap-1">
              <span className="text-[10px] text-stone-400 mr-1">Focus:</span>
              {[
                { key: 'local', label: 'Local' },
                { key: 'regional', label: 'Regional' },
                { key: 'territory', label: 'Territory' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setZoneFocus(opt.key)}
                  className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
                    zoneFocus === opt.key
                      ? 'bg-stone-800 text-white'
                      : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                  }`}
                  data-testid={`zone-focus-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <p className="text-[10px] text-stone-400 ml-5 mt-1 leading-tight">
            {!winZonesMode && 'Strategic overlay for coverage & opportunity'}
            {winZonesMode === 'market' && 'Where the biggest markets are'}
            {winZonesMode === 'coverage' && 'Where you ARE — your existing footprint'}
            {winZonesMode === 'opportunity' && 'Where you\'re NOT — highest density gaps'}
          </p>
        </div>
      )}

      {/* Win Zone Settings */}
      {hasData && winZonesMode && (
        <div className="px-4 py-2 border-b border-stone-100">
          <button
            onClick={() => setShowWinZoneSettings(v => !v)}
            className="flex items-center gap-1.5 w-full text-left py-1"
            data-testid="win-zone-settings-toggle"
          >
            {showWinZoneSettings ? <ChevronDown className="w-3 h-3 text-stone-400" /> : <ChevronRight className="w-3 h-3 text-stone-400" />}
            <span className="text-[10px] tracking-[0.08em] uppercase font-semibold text-stone-400">Win Zone Settings</span>
          </button>
          {showWinZoneSettings && (
            <div className="mt-2 space-y-2.5">
              {[
                { key: 'densityFloor', label: 'Density Floor', min: 0.4, max: 0.9, step: 0.05, fmt: (v) => `${Math.round(v * 100)}th pctile` },
                { key: 'coverageRadiusMi', label: 'Coverage Radius', min: 15, max: 60, step: 5, fmt: (v) => `${v} mi` },
                { key: 'minClusterSize', label: 'Min Cluster Size', min: 3, max: 15, step: 1, fmt: (v) => `${v} counties` },
                { key: 'targetZones', label: 'Target Zones', min: 5, max: 25, step: 1, fmt: (v) => `${v}` },
              ].map(({ key, label, min, max, step, fmt }) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-[10px] text-stone-500 w-24">{label}</span>
                  <input
                    type="range" min={min} max={max} step={step}
                    value={winZoneSettings[key]}
                    onChange={(e) => setWinZoneSettings(prev => ({ ...prev, [key]: parseFloat(e.target.value) }))}
                    className="flex-1 h-1 accent-[#0A2540]"
                    data-testid={`wz-slider-${key}`}
                  />
                  <span className="text-[10px] text-stone-500 w-20 text-right">{fmt(winZoneSettings[key])}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Win Zone Cards */}
      {hasData && winZonesMode && winClusters.length > 0 && (
        <div className="px-4 py-3 border-b border-stone-100">
          <label className="text-[10px] tracking-[0.08em] uppercase font-semibold text-stone-400 block mb-2">
            {winZonesMode === 'market' ? 'Top Market Zones' : winZonesMode === 'coverage' ? 'Top Coverage Zones' : 'Top Opportunity Zones'}
          </label>
          <WinZoneCards
            winClusters={winClusters}
            winZonesMode={winZonesMode}
            onZoomToZone={handleZoomToZone}
          />
        </div>
      )}

      {/* Data Layers (Advanced) */}
      {hasData && (
        <div className="border-b border-stone-100">
          <button onClick={() => setAdvancedOpen(!advancedOpen)} className="flex items-center gap-1.5 w-full px-4 py-2.5 text-left hover:bg-stone-50 transition-colors" data-testid="advanced-toggle">
            {advancedOpen ? <ChevronDown className="w-3 h-3 text-stone-400" /> : <ChevronRight className="w-3 h-3 text-stone-400" />}
            <span className="text-[10px] tracking-[0.08em] uppercase font-semibold text-stone-400">Data Layers (Advanced)</span>
            {activeMarket === 'custom' && <span className="ml-auto text-[9px] bg-stone-200 text-stone-500 px-1.5 py-0.5 rounded">Custom</span>}
          </button>
          {advancedOpen && (
            <div className="px-4 pb-3">
              <div className="mb-3">
                <FileUpload onPointUpload={handlePointUpload} onDensityUpload={handleDensityUpload} loading={loading} />
              </div>
              <LayerControls allLayers={allLayers} activeLayers={activeLayers} onToggle={toggleLayer} radiusSettings={radiusSettings} onRadiusChange={handleRadiusChange} layerColors={layerColors} onColorChange={handleColorChange} />
            </div>
          )}
        </div>
      )}

      {!hasData && (
        <div className="px-4 py-3 border-b border-stone-100">
          <label className="text-[10px] tracking-[0.08em] uppercase font-semibold text-stone-400 block mb-2">Data Upload</label>
          <FileUpload onPointUpload={handlePointUpload} onDensityUpload={handleDensityUpload} loading={loading} />
        </div>
      )}
    </>
  );

  return (
    <div className="h-screen w-full flex flex-col bg-stone-100 overflow-hidden">
      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-2 bg-white border-b border-stone-200 z-20">
        <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>GDM Win Zones</h1>
        <button onClick={() => setMobileOpen(!mobileOpen)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A2540] text-white text-xs font-medium" data-testid="mobile-menu-toggle">
          {mobileOpen ? 'Close' : 'Menu'}
          {activeMarket && activeMarket !== 'custom' && !mobileOpen && <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px] capitalize">{activeMarket.replace('_', ' ')}</span>}
        </button>
      </div>

      {/* Mobile slide-over */}
      {mobileOpen && (
        <div className="md:hidden absolute inset-0 z-30 flex" style={{ top: '48px' }}>
          <div className="w-full max-w-sm bg-white shadow-xl overflow-y-auto" style={{ maxHeight: 'calc(100vh - 48px)' }}>{sidebarContent}</div>
          <div className="flex-1 bg-black/30" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex-1 flex flex-row overflow-hidden">
        {/* Desktop sidebar */}
        <div className="hidden md:flex md:w-72 lg:w-80 flex-shrink-0 border-r border-stone-200 bg-white h-full flex-col z-10 shadow-sm">
          <div className="px-5 pt-5 pb-4 border-b border-stone-100">
            <h1 className="text-2xl tracking-tight font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>GDM Win Zones</h1>
            <p className="text-xs text-stone-400 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Dairy & seed dealer territory analysis</p>
          </div>
          <div className="flex-1 overflow-y-auto">{sidebarContent}</div>
        </div>

        {/* Map */}
        <div className="flex-grow relative h-full bg-stone-50 flex flex-col">
          <MapboxVisualization
            pointData={filteredPointData}
            locationData={filteredLocationData}
            densityData={filteredDensityData}
            activeLayers={activeLayers}
            radiusSettings={radiusSettings}
            layerColors={layerColors}
            winZonesEnabled={winZonesMode}
            winZones={winZones}
            weightedWinZones={[]}
            territoriesEnabled={false}
            visibleReps={{}}
            onWinZoneRankings={setWinZoneRankings}
            onEnrichedFeatures={setEnrichedFeatures}
            onMapZoom={(fn) => { mapZoomRef.current = fn; }}
            selectedStates={selectedStates}
            hasData={hasData}
            winZoneSettings={winZoneSettings}
            onWinClustersComputed={setWinClusters}
          />
        </div>
      </div>
    </div>
  );
};

export default MapDashboard;
